import type { MuhuratResponse } from "@ishvaram/api-client";
import { apiRequest, edgeRead } from "./_fetch";

export type { MuhuratResponse };
export type {
  MuhuratTimePeriod,
  MuhuratPanchangInfo,
  MuhuratVara,
  MuhuratTimeSlot,
} from "@ishvaram/api-client";

export async function getMuhurat(
  date?: string,
  place?: string,
  latitude?: number,
  longitude?: number,
  timezone?: string
): Promise<MuhuratResponse> {
  const params = new URLSearchParams();
  if (date) params.set("date", date);
  if (place) params.set("place", place);
  if (latitude !== undefined) params.set("latitude", String(latitude));
  if (longitude !== undefined) params.set("longitude", String(longitude));
  if (timezone) params.set("timezone", timezone);
  const qs = params.toString();
  return apiRequest<MuhuratResponse>(`/muhurat${qs ? `?${qs}` : ""}`);
}

/**
 * Shubh Muhurat digest — the curated "auspicious times today in <city>" answer.
 * Distinct from panchang (almanac) and choghadiya (raw table): named muhurats
 * (Brahma/Abhijit/Vijaya/Godhuli) + best choghadiya + windows to avoid.
 *
 * Locally typed because the backend ShubhMuhuratResponse is not yet exported by
 * @ishvaram/api-client (run `npm run generate-types` in the package to adopt the
 * generated shape later). Deterministic per (city, date) → served via the
 * /_jyotish/* edge read-cache (rule 38 deterministic-read lane).
 */
export interface ShubhNamedMuhurat {
  key: string;
  name_en: string;
  name_hi: string;
  summary_en: string;
  summary_hi: string;
  start: string;
  end: string;
}

export interface ShubhChoghadiyaWindow {
  name_en: string;
  name_hi: string;
  quality: string;
  start: string;
  end: string;
}

export interface ShubhAvoidWindow {
  key: string;
  name_en: string;
  name_hi: string;
  start: string;
  end: string;
}

export interface ShubhMuhuratResponse {
  date: string;
  place: string;
  sunrise: string;
  sunset: string;
  vara: { name: string; name_hi: string; weekday: number };
  tithi: { name: string; paksha: string };
  nakshatra: { name: string };
  named_muhurats: ShubhNamedMuhurat[];
  auspicious_choghadiya: ShubhChoghadiyaWindow[];
  best_window: ShubhChoghadiyaWindow | null;
  inauspicious: ShubhAvoidWindow[];
}

export async function getShubhMuhurat(params: {
  date?: string;
  latitude?: number;
  longitude?: number;
  /** Offset from UTC in hours. India is 5.5. */
  tz_offset?: number;
  place?: string;
}): Promise<ShubhMuhuratResponse> {
  const q = new URLSearchParams();
  if (params.date) q.set("date", params.date);
  if (params.latitude !== undefined) q.set("latitude", String(params.latitude));
  if (params.longitude !== undefined) q.set("longitude", String(params.longitude));
  if (params.tz_offset !== undefined) q.set("tz_offset", String(params.tz_offset));
  if (params.place) q.set("place", params.place);
  return edgeRead<ShubhMuhuratResponse>("muhurat/shubh", q.toString());
}
